"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Field } from "@base-ui/react/field";
import { forwardRef, useState } from "react";
import { cn } from "../../utils/cn.js";
import { getTooltipSideOffset } from "../Tooltip/utils.js";
import { inputTextVariants } from "./InputText.variants.js";
import { InputTextWrapper } from "./InputTextWrapper.js";
const InputText_InputText = /*#__PURE__*/ forwardRef(function({ label, isLabelHidden, helperText, errorText, maxLength, iconLeft, iconRight, emphasis, isInset, size, className, isDisabled, errorMessage, validate, 'aria-invalid': ariaInvalid, validationMode = 'onBlur', onChange, hasExternalError = false, rightIconButtonProps, ...props }, ref) {
    const { name, ...rest } = props;
    const [internalError, setInternalError] = useState(false);
    const [success, setSuccess] = useState(false);
    const tooltipSideOffset = getTooltipSideOffset(size);
    const hasPlaceholder = Boolean(props.placeholder);
    const hasErrorTextOverride = Boolean(errorText);
    const hasError = hasErrorTextOverride || hasExternalError || internalError;
    const styles = inputTextVariants({
        emphasis,
        isDisabled,
        hasIconLeft: !!iconLeft,
        hasIconRight: !!iconRight,
        isInset,
        hasPlaceholder,
        size,
        error: hasError,
        success: success && !hasError,
        rightIconButton: !!rightIconButtonProps
    });
    function handleChange(event) {
        onChange?.(event);
    }
    const handleValidation = (value)=>{
        if (!hasErrorTextOverride && !hasExternalError && validate && 'string' == typeof value) {
            const isValid = validate(value);
            setInternalError(!isValid);
            setSuccess(isValid);
        }
        return null;
    };
    const getDisplayErrorText = ()=>{
        if (errorText) return errorText;
        if (!errorText && errorMessage && (hasExternalError || internalError)) return errorMessage;
    };
    const displayErrorText = getDisplayErrorText();
    return /*#__PURE__*/ jsxs(Field.Root, {
        name: name,
        className: cn(styles.root(), className),
        validate: handleValidation,
        disabled: isDisabled,
        validationMode: validationMode,
        children: [
            /*#__PURE__*/ jsx(Field.Control, {
                render: (inputProps)=>/*#__PURE__*/ jsx(InputTextWrapper, {
                        ...rest,
                        id: inputProps.id,
                        ref: ref,
                        styles: styles,
                        iconLeft: iconLeft,
                        ariaInvalid: Boolean(ariaInvalid) || hasError,
                        errorText: displayErrorText,
                        maxLength: maxLength,
                        onChange: handleChange,
                        error: hasError,
                        tooltipSideOffset: tooltipSideOffset,
                        errorMessage: errorMessage,
                        success: success && !hasError,
                        iconRight: iconRight,
                        rightIconButtonProps: rightIconButtonProps,
                        isDisabled: isDisabled,
                        size: size
                    })
            }),
            label ? /*#__PURE__*/ jsx(Field.Label, {
                className: cn(styles.labelSlot(), isLabelHidden ? 'hidden' : ''),
                children: label
            }) : null,
            helperText || displayErrorText || maxLength ? /*#__PURE__*/ jsxs("div", {
                className: styles.textSlot(),
                children: [
                    /*#__PURE__*/ jsx(Field.Error, {
                        match: Boolean(displayErrorText) && !errorMessage,
                        className: styles.errorTextSlot(),
                        "aria-live": "assertive",
                        children: displayErrorText
                    }),
                    !displayErrorText && helperText ? /*#__PURE__*/ jsx(Field.Description, {
                        className: styles.helperTextSlot(),
                        "aria-live": "polite",
                        children: helperText
                    }) : null,
                    maxLength && /*#__PURE__*/ jsxs("div", {
                        className: styles.characterCount(),
                        children: [
                            "0/",
                            maxLength
                        ]
                    })
                ]
            }) : null
        ]
    });
});
export { InputText_InputText as InputText };
